/**
 * Reusable validation functions and regex patterns
 */

import { isValidPhoneNumber } from "react-phone-number-input";

/**
 * Common regex patterns for validation
 */
export const REGEX_PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  URL: /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-]*)*$/,
} as const;

/**
 * Validation result type
 */
export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Generic required field validator
 */
export const validateRequired = (
  value: any,
  fieldLabel: string
): ValidationResult => {
  if (!value) {
    return { isValid: false, error: `${fieldLabel} is required` };
  }
  return { isValid: true };
};

/**
 * Email format validator
 */
export const validateEmail = (
  email: string | null | undefined,
  fieldLabel: string = "Email"
): ValidationResult => {
  if (!email) {
    return { isValid: false, error: `${fieldLabel} is required` };
  }
  if (!REGEX_PATTERNS.EMAIL.test(email)) {
    return { isValid: false, error: "Invalid email format" };
  }
  return { isValid: true };
};

/**
 * URL format validator
 */
export const validateUrl = (
  url: string | null | undefined
): ValidationResult => {
  if (!url || !REGEX_PATTERNS.URL.test(url)) {
    return { isValid: false, error: "Invalid website URL" };
  }
  return { isValid: true };
};

/**
 * Phone number validator with international support
 */
export const validatePhoneNumber = (
  phoneNumber: string | null | undefined,
  fieldLabel: string = "Phone number"
): ValidationResult => {
  if (!phoneNumber) {
    return { isValid: false, error: `${fieldLabel} is required` };
  }

  const phoneStr = String(phoneNumber);
  const isValid =
    isValidPhoneNumber(phoneStr) ||
    (!phoneStr.startsWith("+") && isValidPhoneNumber(`+${phoneStr}`));

  if (!isValid) {
    return { isValid: false, error: "Invalid phone number" };
  }
  return { isValid: true };
};

/**
 * Array/file upload validator
 */
export const validateFileUpload = (
  files: any[] | null | undefined,
  fieldLabel: string
): ValidationResult => {
  if (!files || files.length === 0) {
    return { isValid: false, error: `${fieldLabel} is required` };
  }
  return { isValid: true };
};

/**
 * Conditional field validator (validates only if condition is true)
 */
export const validateConditional = (
  condition: boolean,
  value: any,
  fieldLabel: string
): ValidationResult => {
  if (condition && !value) {
    return { isValid: false, error: `${fieldLabel} is required` };
  }
  return { isValid: true };
};

/**
 * Field equality validator
 */
export const validateEquals = (
  value1: string | null | undefined,
  value2: string | null | undefined,
  fieldLabel: string,
  expectedLabel: string
): ValidationResult => {
  if (value1 && value2 && value1 !== value2) {
    return {
      isValid: false,
      error: `${fieldLabel} must match ${expectedLabel}`,
    };
  }
  return { isValid: true };
};

/**
 * Helper to add error to errors object
 */
export const addError = (
  errors: { [key: string]: string },
  fieldName: string,
  errorMessage: string
): void => {
  errors[fieldName] = errorMessage;
};

/**
 * Helper to clean up empty error sections
 */
export const cleanupEmptyErrors = (errors: any): void => {
  Object.keys(errors).forEach((key) => {
    if (
      typeof errors[key] === "object" &&
      Object.keys(errors[key]).length === 0
    ) {
      delete errors[key];
    }
  });
};
